import { InfiniteData, UseInfiniteQueryOptions, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { QueryKeys } from '../../enums/queryKeys';
import { UserRole } from '../../enums/userEnums';

export interface GroupReference {
  id: number;
  name: string;
}

export interface GroupMember {
  id: number;
  name: string;
  title: string;
  email: string;
  access: UserRole;
  groups: GroupReference[];
}

export interface GroupManagerGroup {
  id: number;
  name: string;
  iconUrl: string;
  createdAt: string;
  members: GroupMember[];
}

export interface GroupManagerPage {
  groups: GroupManagerGroup[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  nextOffset: number | null;
}

export interface GroupIconReference {
  id: number;
  url: string;
}

export interface GroupFileTreeFolder {
  id: number;
  name: string;
  folders: GroupFileTreeFolder[];
  files: GroupReference[];
}

// The client's file manager contents below its root folder.
export interface GroupFileTree {
  folders: GroupFileTreeFolder[];
  files: GroupReference[];
}

export interface CreateGroupPayload {
  name: string;
  iconUrl: string;
  userIds: number[];
  mapIds: number[];
  folderIds: number[];
  fileIds: number[];
}

// Everything the group form edits, as returned by GET /groups/:id.
export interface GroupDetails {
  id: number;
  name: string;
  iconUrl: string;
  userIds: number[];
  mapIds: number[];
  folderIds: number[];
  fileIds: number[];
}

export interface EditGroupResponse extends GroupDetails {
  message: string;
}

export interface CreateGroupResponse {
  message: string;
  id: number;
  name: string;
  iconUrl: string;
  createdAt: string;
  userIds: number[];
}

export interface DeleteGroupResponse {
  message: string;
  id: number;
  deletedAt: string;
}

export interface RemoveUserResponse {
  message: string;
  groupId: number;
  userId: number;
}

export interface DuplicateUserResponse {
  message: string;
  userId: number;
  addedToGroupIds: number[];
}

export interface MoveUserResponse {
  message: string;
  userId: number;
  removedFromGroupId: number;
  addedToGroupIds: number[];
}

export interface GroupManagerApiErrorDetail {
  field?: string;
  message?: string;
  invalidIds?: number[];
}

export interface GroupManagerApiErrorBody {
  errorCode?: string;
  message?: string;
  error?: string;
  details?: GroupManagerApiErrorDetail[];
}

export class GroupManagerError extends Error {
  readonly errorCode?: string;
  readonly status?: number;
  readonly details?: GroupManagerApiErrorBody['details'];

  constructor(message: string, errorCode?: string, status?: number, details?: GroupManagerApiErrorBody['details']) {
    super(message);
    this.name = 'GroupManagerError';
    this.errorCode = errorCode;
    this.status = status;
    this.details = details;
  }
}

// No UNAUTHORIZED entry: axiosInstance's response interceptor already handles
// every 401 (session clear + redirect) and suppresses all other notifications
// for the rest of the page's life, so this fallback would never be shown.
const FALLBACK_MESSAGES: Record<string, string> = {
  FORBIDDEN: 'Insufficient permissions.',
  GROUP_NOT_FOUND: 'Group not found or already deleted.',
  USER_MEMBERSHIP_NOT_FOUND: 'User is not a member of this group.',
  USER_NOT_FOUND: 'User not found.',
  VALIDATION_ERROR: 'Select at least one group.',
  INVALID_REFERENCE: 'One or more selected groups are invalid.',
};

const MOVE_FALLBACK_MESSAGES: Record<string, string> = {
  ...FALLBACK_MESSAGES,
  GROUP_NOT_FOUND: 'Source group not found.',
  USER_MEMBERSHIP_NOT_FOUND: 'User is not a member of the source group.',
  VALIDATION_ERROR: 'Select at least one target group.',
};

const DELETE_FALLBACK_MESSAGES: Record<string, string> = {
  ...FALLBACK_MESSAGES,
  FORBIDDEN: 'Insufficient permissions to delete this group.',
};

const LIST_FALLBACKS: Record<string, string> = {
  FORBIDDEN: "You don't have access to these groups.",
};

// Create and edit. No VALIDATION_ERROR entry: the shared one is about picking target
// groups, so these fall through to the API's own message (e.g. duplicate or missing name).
const SAVE_FALLBACK_MESSAGES: Record<string, string> = {
  FORBIDDEN: 'Insufficient permissions to save groups.',
  GROUP_NOT_FOUND: 'Group not found or already deleted.',
  INVALID_REFERENCE: 'One or more selected users, maps, folders or files are invalid.',
};

type GroupManagerOperation = 'list' | 'mutation' | 'move' | 'delete' | 'save';

const OPERATION_FALLBACKS: Record<GroupManagerOperation, Record<string, string>> = {
  list: LIST_FALLBACKS,
  mutation: FALLBACK_MESSAGES,
  move: MOVE_FALLBACK_MESSAGES,
  delete: DELETE_FALLBACK_MESSAGES,
  save: SAVE_FALLBACK_MESSAGES,
};

// The API returns accessRole as an uppercase code (ADMIN/EDITOR/VIEWER); UserRole's values are display-cased.
const ACCESS_ROLE_MAP: Record<string, UserRole> = {
  ADMIN: UserRole.ADMIN,
  EDITOR: UserRole.EDITOR,
  VIEWER: UserRole.VIEWER,
};

export interface RawGroupMember {
  id: number;
  name: string;
  title: string | null;
  email: string;
  accessRole: string;
  groups: GroupReference[];
}

export interface RawGroup {
  id: number;
  name: string;
  iconUrl: string;
  createdAt: string;
  users: RawGroupMember[];
}

export interface RawGroupManagerListResponse {
  groups: RawGroup[];
  pagination: GroupManagerPage['pagination'];
}

export interface RawGroupManagerMetaGroupsResponse {
  groups: GroupReference[];
}

export interface RawGroupManagerMetaUsersResponse {
  users: GroupReference[];
}

export interface RawGroupManagerMetaIconsResponse {
  icons: GroupIconReference[];
}

export interface RawGroupManagerMetaMapsResponse {
  maps: GroupReference[];
}

const normalizeGroup = (group: RawGroup): GroupManagerGroup => ({
  id: group.id,
  name: group.name,
  iconUrl: group.iconUrl ?? '',
  createdAt: group.createdAt ?? '',
  members: (group.users ?? []).map((user) => ({
    id: user.id,
    name: user.name,
    title: user.title ?? '',
    email: user.email,
    access: ACCESS_ROLE_MAP[user.accessRole] ?? UserRole.VIEWER,
    groups: user.groups,
  })),
});

const normalizeGroupManagerError = (error: unknown, operation: GroupManagerOperation = 'mutation'): GroupManagerError => {
  if (error instanceof Error && (error.name === 'CanceledError' || error.name === 'AbortError')) return error as GroupManagerError;
  const axiosError = error as AxiosError<GroupManagerApiErrorBody>;
  const body = axiosError.response?.data;
  const errorCode = body?.errorCode;
  const fallback = OPERATION_FALLBACKS[operation][errorCode ?? ''] ?? body?.message ?? body?.error;
  return new GroupManagerError(fallback ?? 'Unable to complete the group manager request.', errorCode, axiosError.response?.status, body?.details);
};

const withPath = (path: string, values: Record<string, number>) =>
  Object.entries(values).reduce((result, [key, value]) => result.replace(`:${key}`, String(value)), path);

const GroupManagerService = () => {
  const getGroupsList = (limit = 20): UseInfiniteQueryOptions<GroupManagerPage, GroupManagerError, InfiniteData<GroupManagerPage, number>, [string], number> => ({
    queryKey: [QueryKeys.GET_GROUP_MANAGER_GROUPS],
    initialPageParam: 0,
    queryFn: async ({ pageParam, signal }) => {
      try {
        const { data } = await axiosInstance.get<RawGroupManagerListResponse>(ApiRoutes.GROUP_MANAGER_GROUPS, {
          params: { limit, offset: pageParam },
          signal,
        });
        const pagination = data.pagination;
        return {
          groups: data.groups.map(normalizeGroup),
          pagination,
          nextOffset: pagination.hasMore ? pagination.offset + pagination.limit : null,
        };
      } catch (error) {
        throw normalizeGroupManagerError(error, 'list');
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    // Live admin listing: every open should show current data, not a cached one.
    staleTime: 0,
  });

  // Unpaginated: backs the duplicate/move pickers, which need every group, not just loaded pages.
  const getGroupOptions = (): UseQueryOptions<GroupReference[], GroupManagerError> => ({
    queryKey: [QueryKeys.GET_GROUP_MANAGER_META_GROUPS],
    queryFn: async ({ signal }) => {
      try {
        const { data } = await axiosInstance.get<RawGroupManagerMetaGroupsResponse>(ApiRoutes.GROUP_MANAGER_META_GROUPS, { signal });
        return data.groups;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'list');
      }
    },
  });

  // These back the create-group form's pickers.
  const getUserOptions = (): UseQueryOptions<GroupReference[], GroupManagerError> => ({
    queryKey: [QueryKeys.GET_GROUP_MANAGER_META_USERS],
    queryFn: async ({ signal }) => {
      try {
        const { data } = await axiosInstance.get<RawGroupManagerMetaUsersResponse>(ApiRoutes.GROUP_MANAGER_META_USERS, { signal });
        return data.users;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'list');
      }
    },
  });

  const getGroupIcons = (): UseQueryOptions<GroupIconReference[], GroupManagerError> => ({
    queryKey: [QueryKeys.GET_GROUP_MANAGER_META_ICONS],
    queryFn: async ({ signal }) => {
      try {
        const { data } = await axiosInstance.get<RawGroupManagerMetaIconsResponse>(ApiRoutes.GROUP_MANAGER_META_ICONS, { signal });
        return data.icons;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'list');
      }
    },
  });

  const getMapOptions = (): UseQueryOptions<GroupReference[], GroupManagerError> => ({
    queryKey: [QueryKeys.GET_GROUP_MANAGER_META_MAPS],
    queryFn: async ({ signal }) => {
      try {
        const { data } = await axiosInstance.get<RawGroupManagerMetaMapsResponse>(ApiRoutes.GROUP_MANAGER_META_MAPS, { signal });
        return data.maps;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'list');
      }
    },
  });

  const getFileTree = (): UseQueryOptions<GroupFileTree, GroupManagerError> => ({
    queryKey: [QueryKeys.GET_GROUP_MANAGER_META_FILES],
    queryFn: async ({ signal }) => {
      try {
        const { data } = await axiosInstance.get<GroupFileTree>(ApiRoutes.GROUP_MANAGER_META_FILES, { signal });
        return data;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'list');
      }
    },
  });

  const createGroup = (): UseMutationOptions<CreateGroupResponse, GroupManagerError, CreateGroupPayload> => ({
    mutationFn: async (payload) => {
      try {
        const { data } = await axiosInstance.post<CreateGroupResponse>(ApiRoutes.GROUP_MANAGER_GROUPS, payload);
        return data;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'save');
      }
    },
  });

  const getGroupDetails = (groupId: number): UseQueryOptions<GroupDetails, GroupManagerError> => ({
    queryKey: [QueryKeys.GET_GROUP_MANAGER_GROUP_DETAILS, groupId],
    queryFn: async ({ signal }) => {
      try {
        const { data } = await axiosInstance.get<GroupDetails>(withPath(ApiRoutes.GROUP_MANAGER_GROUP_BY_ID, { id: groupId }), { signal });
        return data;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'save');
      }
    },
    // The edit form is prefilled from this, so it must reflect the latest saved state.
    staleTime: 0,
  });

  const editGroup = (): UseMutationOptions<EditGroupResponse, GroupManagerError, { groupId: number } & CreateGroupPayload> => ({
    mutationFn: async ({ groupId, ...payload }) => {
      try {
        const { data } = await axiosInstance.put<EditGroupResponse>(withPath(ApiRoutes.GROUP_MANAGER_GROUP_BY_ID, { id: groupId }), payload);
        return data;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'save');
      }
    },
  });

  const deleteGroup = (): UseMutationOptions<DeleteGroupResponse, GroupManagerError, { groupId: number }> => ({
    mutationFn: async ({ groupId }) => {
      try {
        const { data } = await axiosInstance.delete<DeleteGroupResponse>(withPath(ApiRoutes.GROUP_MANAGER_GROUP_BY_ID, { id: groupId }));
        return data;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'delete');
      }
    },
  });

  const removeUserFromGroup = (): UseMutationOptions<RemoveUserResponse, GroupManagerError, { groupId: number; userId: number }> => ({
    mutationFn: async ({ groupId, userId }) => {
      try {
        const { data } = await axiosInstance.delete<RemoveUserResponse>(withPath(ApiRoutes.GROUP_MANAGER_MEMBERSHIP, { groupId, userId }));
        return data;
      } catch (error) {
        throw normalizeGroupManagerError(error);
      }
    },
  });

  const duplicateUser = (): UseMutationOptions<DuplicateUserResponse, GroupManagerError, { userId: number; groupIds: number[] }> => ({
    mutationFn: async ({ userId, groupIds }) => {
      if (groupIds.length === 0) throw new GroupManagerError('Select at least one group.', 'VALIDATION_ERROR', 400);
      try {
        const { data } = await axiosInstance.post<DuplicateUserResponse>(withPath(ApiRoutes.GROUP_MANAGER_DUPLICATE_USER, { userId }), { groupIds });
        return data;
      } catch (error) {
        throw normalizeGroupManagerError(error);
      }
    },
  });

  const moveUser = (): UseMutationOptions<MoveUserResponse, GroupManagerError, { groupId: number; userId: number; targetGroupIds: number[] }> => ({
    mutationFn: async ({ groupId, userId, targetGroupIds }) => {
      if (targetGroupIds.length === 0) throw new GroupManagerError('Select at least one target group.', 'VALIDATION_ERROR', 400);
      try {
        const { data } = await axiosInstance.post<MoveUserResponse>(withPath(ApiRoutes.GROUP_MANAGER_MOVE_USER, { groupId, userId }), { targetGroupIds });
        return data;
      } catch (error) {
        throw normalizeGroupManagerError(error, 'move');
      }
    },
  });

  return { getGroupsList, getGroupOptions, getUserOptions, getGroupIcons, getMapOptions, getFileTree, createGroup, getGroupDetails, editGroup, deleteGroup, removeUserFromGroup, duplicateUser, moveUser };
};

export default GroupManagerService;
